import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Bell, BellOff, Trash2, CheckCircle, Filter } from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';

const FILTERS = [
  { value: 'all',    label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'order',  label: 'Orders' },
  { value: 'system', label: 'System' },
];

const TYPE_COLORS = {
  order:   { color: '#3B82F6', bg: '#EFF6FF', icon: '🛒' },
  payment: { color: '#10B981', bg: '#ECFDF5', icon: '💰' },
  system:  { color: '#64748B', bg: '#F1F5F9', icon: '⚙️' },
  alert:   { color: '#EF4444', bg: '#FEF2F2', icon: '⚠️' },
  review:  { color: '#F59E0B', bg: '#FFFBEB', icon: '⭐' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [toast,    setToast]    = useState('');

  const showMsg = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async (f = filter) => {
    setLoading(true);
    const res = await kitchenApi.getNotifications(f);
    if (res.success) setNotifications(Array.isArray(res.data) ? res.data : (res.data?.notifications || []));
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(filter); }, [filter]);

  const markRead = async (id) => {
    await kitchenApi.markNotificationRead(id);
    setNotifications(prev => prev.map(n => (n.notification_id || n.id) === id ? { ...n, is_read: true } : n));
  };

  const markAllRead = async () => {
    await kitchenApi.markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    showMsg('All marked as read');
  };

  const deleteNotif = async (id) => {
    await kitchenApi.deleteNotification(id);
    setNotifications(prev => prev.filter(n => (n.notification_id || n.id) !== id));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Notifications</h1>
          <p className="k-page-sub">{unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="k-page-actions">
          {unreadCount > 0 && (
            <button className="k-btn k-btn-secondary k-btn-sm" onClick={markAllRead}><CheckCircle size={14} /> Mark all read</button>
          )}
          <button className="k-btn-icon" onClick={() => load(filter)} title="Refresh"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="k-tabs" style={{ marginBottom: 20, maxWidth: 420 }}>
        {FILTERS.map(f => (
          <button
            key={f.value}
            className={`k-tab${filter === f.value ? ' active' : ''}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div>
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="k-card" style={{ marginBottom: 10, display: 'flex', gap: 14, padding: 16 }}>
              <div className="k-skeleton" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="k-skeleton" style={{ height: 14, width: '60%', marginBottom: 8 }} />
                <div className="k-skeleton" style={{ height: 12, width: '80%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="k-card">
          <div className="k-empty">
            <div className="k-empty-icon">🔔</div>
            <div className="k-empty-title">No notifications</div>
            <div className="k-empty-sub">You're all caught up! Notifications will appear here.</div>
          </div>
        </div>
      ) : (
        <div>
          {notifications.map(n => {
            const type  = n.notification_type || n.type || 'system';
            const meta  = TYPE_COLORS[type] || TYPE_COLORS.system;
            const id    = n.notification_id || n.id;
            return (
              <div
                key={id}
                style={{
                  background: n.is_read ? 'var(--k-surface)' : '#FFFBF5',
                  border: `1px solid ${n.is_read ? 'var(--k-border)' : '#FED7AA'}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 10,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  cursor: !n.is_read ? 'pointer' : 'default',
                }}
                onClick={() => !n.is_read && markRead(id)}
              >
                {/* Icon */}
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: meta.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>
                  {meta.icon}
                </div>
                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: n.is_read ? 500 : 700, fontSize: 14, color: 'var(--k-text)' }}>
                      {n.title || n.subject || 'Notification'}
                    </div>
                    {!n.is_read && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--k-primary)', flexShrink: 0, marginLeft: 8, marginTop: 4 }} />
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--k-text-sub)', lineHeight: 1.5, margin: 0 }}>
                    {n.message || n.body || n.content}
                  </p>
                  <div style={{ fontSize: 11, color: 'var(--k-text-muted)', marginTop: 6 }}>
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  </div>
                </div>
                {/* Delete */}
                <button
                  className="k-btn-icon"
                  style={{ padding: 6, color: 'var(--k-text-muted)', flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); deleteNotif(id); }}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <div className="k-toast-container">
          <div className="k-toast success">✓ {toast}</div>
        </div>
      )}
    </div>
  );
}
